/** RPE histogram axis: 5 → 10 in 0.5 steps (11 bars). */
const RPE_STEPS = [5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10];

/** RiR axis (RiR = 10 − RPE): 5 → 0 in 0.5 steps (11 bars). */
const RIR_STEPS = [5, 4.5, 4, 3.5, 3, 2.5, 2, 1.5, 1, 0.5, 0];

/** %1RM zone ids for per-lift intensity bars (panel i18n keys). */
export const INTENSITY_ZONE_IDS = Object.freeze([
  "lt60",
  "r60_75",
  "r75_85",
  "gt85",
]);

/** @param {number[]} counts — length 11, same order as RPE_STEPS. */
export function buildRpeBuckets(counts) {
  return RPE_STEPS.map((step, i) => ({
    step,
    count: counts[i] ?? 0,
  }));
}

/** Function to build RIR buckets from bin counts.
* @param {number[]} counts — length 11, same order as RIR_STEPS. */
export function buildRirBuckets(counts) {
  return RIR_STEPS.map((step, i) => ({
    step,
    count: counts[i] ?? 0,
  }));
}

/*
* Function to get the intensity zone id for a given percentage.
* @param {number} pct
* @returns {string|null}
*/
export function intensityZoneIdForPercent(pct) {
  if (!Number.isFinite(pct) || pct <= 0) {
    return null;
  }
  if (pct < 60) {
    return "lt60";
  }
  if (pct < 75) {
    return "r60_75";
  }
  if (pct < 85) {
    return "r75_85";
  }
  return "gt85";
}

/** Function to create empty intensity zone counts.
* @returns {Record<string, number>} */
export function createEmptyIntensityZoneCounts() {
  return Object.fromEntries(INTENSITY_ZONE_IDS.map((id) => [id, 0]));
}

/** Function to get the max count among buckets.
* @param {ReadonlyArray<{ count: number }>} buckets */
export function insightsMaxCountAmongBuckets(buckets) {
  return Math.max(...buckets.map((b) => b.count), 1);
}

/** Function to get the normalized bar height percentage.
* @param {number} count @param {number} maxCount */
export function insightsNormalizedBarHeightPct(count, maxCount) {
  if (maxCount < 1) {
    return 0;
  }
  return (count / maxCount) * 100;
}
